import { Box, Checkbox, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BuildChecklistEntity,
  ChecklistItemEntity,
  PreferenceCardEntity,
} from '@peritasai/repository';
import React from 'react';

const TrayListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const TrayGroupContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const TrayGroupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TrayItemContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'disabled',
})<{ isSelected?: boolean; disabled?: boolean }>(
  ({ theme, isSelected, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${
      isSelected ? theme.palette.primary.main : theme.palette.divider
    }`,
    backgroundColor: isSelected
      ? theme.palette.primary.light + '10'
      : theme.palette.background.paper,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: disabled
        ? theme.palette.divider
        : theme.palette.primary.main,
      backgroundColor: disabled
        ? theme.palette.action.disabledBackground
        : theme.palette.action.hover,
    },
    opacity: disabled ? 0.5 : 1,
  })
);

const TrayItemInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: 4,
});

const TrayItemLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
}));

const TrayItemDetails = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 1,
}));

interface TraySelectionStepProps {
  preferenceCard: PreferenceCardEntity;
  buildChecklist: BuildChecklistEntity;
  onTraySelect: (checklistItem: ChecklistItemEntity) => Promise<void>;
  isLoading?: boolean;
}

export const TraySelectionStep: React.FC<TraySelectionStepProps> = ({
  preferenceCard,
  buildChecklist,
  onTraySelect,
  isLoading = false,
}) => {
  const trays = preferenceCard.trackedAssets.trays;
  const checklistGroupByCatalogId = React.useMemo(() => {
    const map = new Map<
      string,
      ReturnType<BuildChecklistEntity['getTrays']>[number] | undefined
    >();
    buildChecklist.getTrays().forEach((group) => {
      map.set(group.catalogItemId, group);
    });
    return map;
  }, [buildChecklist]);

  const handleTrayItemClick = async (checklistItem?: ChecklistItemEntity) => {
    if (isLoading || !checklistItem) return;
    // When user checks an item, it will be allocated and get assetId
    await onTraySelect(checklistItem);
  };

  if (trays.length === 0) {
    return (
      <Box sx={{ padding: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No trays available for selection
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress size={40} />
        </LoadingOverlay>
      )}
      <TrayListContainer>
        {trays.map((trayAsset) => {
          const checklistGroup = checklistGroupByCatalogId.get(
            trayAsset.catalogItemId
          );
          const checklistItems = checklistGroup?.items ?? [];
          const rowCount = Math.max(
            trayAsset.quantity || 1,
            checklistItems.length || 0,
            1
          );

          return (
            <TrayGroupContainer key={trayAsset.catalogItemId}>
              <TrayGroupHeader>
                <Typography variant="h6" fontWeight={600}>
                  {trayAsset.name}
                </Typography>
                {trayAsset.productNumber && (
                  <Typography variant="caption" color="text.secondary">
                    Product: {trayAsset.productNumber}
                    {trayAsset.manufacturer && ` • ${trayAsset.manufacturer}`}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Qty Required: {trayAsset.quantity}, available:{' '}
                  {trayAsset.availableCount}
                </Typography>
              </TrayGroupHeader>
              {Array.from({ length: rowCount }).map((_, index) => {
                const checklistItem = checklistItems[index];
                const isSelected = checklistItem?.allocated ?? false;
                const isDisabled = !checklistItem;

                return (
                  <TrayItemContainer
                    key={`${trayAsset.catalogItemId}-${index}`}
                    isSelected={isSelected}
                    disabled={isDisabled}
                    onClick={() => handleTrayItemClick(checklistItem)}
                  >
                    <TrayItemInfo>
                      <TrayItemLabel>
                        {checklistItem?.assetId ||
                          `${trayAsset.name} #${index + 1}`}
                      </TrayItemLabel>
                      {checklistItem?.notes && (
                        <TrayItemDetails>{checklistItem.notes}</TrayItemDetails>
                      )}
                      {isDisabled && (
                        <TrayItemDetails>
                          Checklist item not available yet
                        </TrayItemDetails>
                      )}
                    </TrayItemInfo>
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => handleTrayItemClick(checklistItem)}
                      color="primary"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TrayItemContainer>
                );
              })}
            </TrayGroupContainer>
          );
        })}
      </TrayListContainer>
    </Box>
  );
};
